<template>
  <div class="category-manager-modal">
    <div v-if="drafts.length" class="category-manager-modal__list">
      <div v-for="category in drafts" :key="category.id" class="category-manager-modal__item">
        <TextInput
          :id="`category_name_${category.id}`"
          label="Naam"
          v-model="category.name"
          placeholder="bijv. Zonwering, Screens"
        />
        <TextInput
          :id="`category_slug_${category.id}`"
          label="Slug"
          v-model="category.slug"
          :placeholder="(category.name || '').toLowerCase().replace(/\s+/g, '-')"
        />
        <div class="form-field">
          <label>Kleur</label>
          <div class="color-picker">
            <input type="color" v-model="category.color" />
            <span class="color-preview" :style="{ backgroundColor: category.color }"></span>
            <span>{{ category.color }}</span>
          </div>
        </div>
        <div class="category-manager-modal__actions">
          <Btn
            variant="secondary"
            type="button"
            :loading="savingCategoryId === category.id"
            @click="handleSave(category)"
          >
            Wijzigingen opslaan
          </Btn>
          <Btn
            variant="danger"
            type="button"
            :loading="deletingCategoryId === category.id"
            @click="handleDelete(category)"
          >
            Verwijderen
          </Btn>
        </div>
      </div>
    </div>
    <p v-else class="category-manager-modal__empty">Nog geen categorieën beschikbaar.</p>

    <div class="category-manager-modal__divider"></div>
    <h4>Categorie toevoegen</h4>
    <TextInput
      id="category_name"
      label="Naam"
      v-model="createForm.name"
      placeholder="bijv. Zonwering, Screens"
      :rules="['required']"
    />
    <TextInput
      id="category_slug"
      label="Slug"
      v-model="createForm.slug"
      :placeholder="(createForm.name || '').toLowerCase().replace(/\s+/g, '-')"
    />
    <small>Laat leeg voor automatische generatie</small>

    <div class="form-field">
      <label>Kleur</label>
      <div class="color-picker">
        <input type="color" v-model="createForm.color" />
        <span class="color-preview" :style="{ backgroundColor: createForm.color }"></span>
        <span>{{ createForm.color }}</span>
      </div>
    </div>

    <div class="category-manager-modal__footer">
      <Btn variant="secondary" type="button" @click="handleClose">Sluiten</Btn>
      <Btn
        variant="primary"
        type="button"
        :loading="creatingCategory"
        :disabled="!createForm.name.trim()"
        @click="handleCreate"
      >
        Aanmaken
      </Btn>
    </div>
  </div>
</template>

<script>
  import { reactive, ref, watch } from 'vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import Btn from '@/components/partials/Btn.vue';

  export default {
    name: 'CategoryManagerModal',
    components: {
      TextInput,
      Btn,
    },
    props: {
      categories: {
        type: Array,
        default: () => [],
      },
      onCreate: {
        type: Function,
        default: null,
      },
      onUpdate: {
        type: Function,
        default: null,
      },
      onDelete: {
        type: Function,
        default: null,
      },
      onClose: {
        type: Function,
        default: null,
      },
    },
    setup(props) {
      const drafts = ref([]);
      const savingCategoryId = ref(null);
      const deletingCategoryId = ref(null);
      const creatingCategory = ref(false);
      const createForm = reactive({
        name: '',
        slug: '',
        color: '#3B82F6',
      });

      const slugify = (name) =>
        (name || '')
          .toString()
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '-');

      const syncDrafts = () => {
        drafts.value = (props.categories || []).map((category) => ({
          ...category,
          slug: category.slug || slugify(category.name),
          color: category.color || '#3B82F6',
        }));
      };

      watch(
        () => props.categories,
        () => syncDrafts(),
        { immediate: true, deep: true }
      );

      const handleCreate = async () => {
        if (!createForm.name.trim() || !props.onCreate) return;
        creatingCategory.value = true;
        try {
          const created = await props.onCreate({
            name: createForm.name.trim(),
            slug: createForm.slug.trim() || slugify(createForm.name),
            color: createForm.color || '#3B82F6',
          });
          if (created) {
            drafts.value.push(created);
          }
          createForm.name = '';
          createForm.slug = '';
          createForm.color = '#3B82F6';
        } finally {
          creatingCategory.value = false;
        }
      };

      const handleSave = async (draft) => {
        if (!draft?.id || !draft.name.trim() || !props.onUpdate) return;
        savingCategoryId.value = draft.id;
        try {
          const updated = await props.onUpdate({
            ...draft,
            name: draft.name.trim(),
            slug: draft.slug?.trim() || slugify(draft.name),
            color: draft.color || '#3B82F6',
          });
          if (updated) {
            drafts.value = drafts.value.map((item) => (item.id === updated.id ? updated : item));
          }
        } finally {
          savingCategoryId.value = null;
        }
      };

      const handleDelete = async (draft) => {
        if (!draft?.id || !props.onDelete) return;
        if (
          !confirm(
            `Weet je zeker dat je categorie "${draft.name}" wilt verwijderen? Bestaande producten raken deze categorie kwijt.`
          )
        ) {
          return;
        }

        deletingCategoryId.value = draft.id;
        try {
          const removed = await props.onDelete(draft);
          if (removed !== false) {
            drafts.value = drafts.value.filter((item) => item.id !== draft.id);
          }
        } finally {
          deletingCategoryId.value = null;
        }
      };

      const handleClose = () => {
        props.onClose?.();
      };

      return {
        drafts,
        createForm,
        creatingCategory,
        savingCategoryId,
        deletingCategoryId,
        handleCreate,
        handleSave,
        handleDelete,
        handleClose,
      };
    },
  };
</script>

<style scoped lang="scss">
  .category-manager-modal {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;

    &__list {
      display: flex;
      flex-direction: column;
      gap: 0.875rem;
    }

    &__item {
      border: 1px solid var(--slate-200);
      border-radius: 8px;
      padding: 0.875rem;
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.625rem;
    }

    &__empty {
      margin: 0;
      color: var(--slate-600);
      font-size: 0.875rem;
    }

    &__divider {
      height: 1px;
      background: var(--slate-200);
      margin: 0.25rem 0;
    }

    &__footer {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      padding-top: 0.875rem;
      border-top: 1px solid var(--slate-200);
    }
  }

  .color-picker {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    input[type='color'] {
      width: 40px;
      height: 40px;
      border: none;
      border-radius: 6px;
      cursor: pointer;
    }

    .color-preview {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 1px solid rgb(0 0 0 / 0.1);
    }
  }
</style>
